import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import { Badge } from '@creanizer/ui'
import { X } from 'lucide-react'
import type { RelationshipResult, CharacterNode, RelationshipEdge } from '@creanizer/types'
import { RELATIONSHIP_TYPE_COLORS, RELATIONSHIP_TYPE_LABELS } from '@creanizer/types'
// AI分析は使用せず、キーワードベースの判定を強化

interface D3RelationshipGraphProps {
  result: RelationshipResult
  width?: number
  height?: number
}

export function D3RelationshipGraph({ result, width = 1000, height = 700 }: D3RelationshipGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [selectedNode, setSelectedNode] = useState<CharacterNode | null>(null)
  const [selectedEdge, setSelectedEdge] = useState<RelationshipEdge | null>(null)
  
  // 感情テキストから関係性タイプを推測する関数（キーワードを強化）
  const detectRelationType = (text: string): keyof typeof RELATIONSHIP_TYPE_COLORS => {
    if (!text) return 'other'
    
    const lowerText = text.toLowerCase()
    
    // より包括的なキーワードベースで判定（優先順位順）
    
    // 敵対系 (enemy) - 最優先で判定
    if (lowerText.includes('敵') || lowerText.includes('憎') || lowerText.includes('嫌い') ||
        lowerText.includes('恨') || lowerText.includes('対立') || lowerText.includes('反目') ||
        lowerText.includes('不倶戴天') || lowerText.includes('宿敵') || lowerText.includes('仇') ||
        lowerText.includes('復讐') || lowerText.includes('怒り') || lowerText.includes('殺意') ||
        lowerText.includes('殺し') || lowerText.includes('倒す') || lowerText.includes('許さない') ||
        lowerText.includes('憤') || lowerText.includes('呪') || lowerText.includes('邪魔') ||
        lowerText.includes('妨害') || lowerText.includes('裏切') || lowerText.includes('背信') ||
        lowerText.includes('宿怨') || lowerText.includes('因縁') || lowerText.includes('報復') ||
        lowerText.includes('敵視') || lowerText.includes('敵意') || lowerText.includes('悪意') ||
        lowerText.includes('害意') || lowerText.includes('脅威')) {
      return 'enemy'
    }
    
    // 恋愛・愛情系 (lover)
    if (lowerText.includes('恋') || lowerText.includes('愛') || lowerText.includes('好き') || 
        lowerText.includes('惚') || lowerText.includes('慕') || lowerText.includes('想い') ||
        lowerText.includes('恋人') || lowerText.includes('彼氏') || lowerText.includes('彼女') ||
        lowerText.includes('片思') || lowerText.includes('両思') || lowerText.includes('婚約') ||
        lowerText.includes('結婚') || lowerText.includes('夫') || lowerText.includes('妻') ||
        lowerText.includes('愛し') || lowerText.includes('愛する') || lowerText.includes('愛され') ||
        lowerText.includes('恋心') || lowerText.includes('恋愛') || lowerText.includes('ラブ') ||
        lowerText.includes('デート') || lowerText.includes('交際') || lowerText.includes('告白')) {
      return 'lover'
    }
    
    // 家族系 (family)
    if (lowerText.includes('家族') || lowerText.includes('親') || lowerText.includes('父') || 
        lowerText.includes('母') || lowerText.includes('兄') || lowerText.includes('姉') || 
        lowerText.includes('弟') || lowerText.includes('妹') || lowerText.includes('祖父') ||
        lowerText.includes('祖母') || lowerText.includes('叔父') || lowerText.includes('叔母') ||
        lowerText.includes('従兄') || lowerText.includes('従姉') || lowerText.includes('息子') ||
        lowerText.includes('娘') || lowerText.includes('孫') || lowerText.includes('血縁') ||
        lowerText.includes('親子') || lowerText.includes('兄弟') || lowerText.includes('姉妹') ||
        lowerText.includes('家庭') || lowerText.includes('肉親') || lowerText.includes('親族')) {
      return 'family'
    }
    
    // ライバル系 (rival)
    if (lowerText.includes('ライバル') || lowerText.includes('競') || lowerText.includes('好敵手') ||
        lowerText.includes('切磋琢磨') || lowerText.includes('競争') || lowerText.includes('対抗') ||
        lowerText.includes('負けたくない') || lowerText.includes('勝負') || lowerText.includes('闘') ||
        lowerText.includes('張り合') || lowerText.includes('対等') || lowerText.includes('拮抗')) {
      return 'rival'
    }
    
    // 師弟系 (mentor)
    if (lowerText.includes('師') || lowerText.includes('弟子') || lowerText.includes('先生') ||
        lowerText.includes('教師') || lowerText.includes('生徒') || lowerText.includes('指導') ||
        lowerText.includes('教え') || lowerText.includes('学') || lowerText.includes('先輩') ||
        lowerText.includes('後輩') || lowerText.includes('メンター') || lowerText.includes('師匠') ||
        lowerText.includes('修行') || lowerText.includes('稽古') || lowerText.includes('伝授') ||
        lowerText.includes('継承') || lowerText.includes('育成') || lowerText.includes('養成')) {
      return 'mentor'
    }
    
    // 幼馴染系 (childhood_friend)
    if (lowerText.includes('幼馴染') || lowerText.includes('幼なじみ') || lowerText.includes('幼い頃') ||
        lowerText.includes('子供の頃') || lowerText.includes('昔から') || lowerText.includes('小さい頃') ||
        lowerText.includes('ガキの頃') || lowerText.includes('昔馴染')) {
      return 'childhood_friend'
    }
    
    // 元恋人系 (ex_lover)
    if (lowerText.includes('元恋人') || lowerText.includes('元カレ') || lowerText.includes('元カノ') ||
        lowerText.includes('別れ') || lowerText.includes('過去の恋') || lowerText.includes('昔の恋') ||
        lowerText.includes('かつての恋') || lowerText.includes('元夫') || lowerText.includes('元妻') ||
        lowerText.includes('離婚') || lowerText.includes('破局')) {
      return 'ex_lover'
    }
    
    // 憧れ・尊敬系 (admirer)
    if (lowerText.includes('憧') || lowerText.includes('尊敬') || lowerText.includes('崇拝') ||
        lowerText.includes('リスペクト') || lowerText.includes('目標') || lowerText.includes('理想') ||
        lowerText.includes('ファン') || lowerText.includes('信奉') || lowerText.includes('敬') ||
        lowerText.includes('見上げ') || lowerText.includes('仰') || lowerText.includes('慕う')) {
      return 'admirer'
    }
    
    // 上下関係系 (superior/subordinate)
    if (lowerText.includes('上司') || lowerText.includes('部下') || lowerText.includes('上官') ||
        lowerText.includes('部長') || lowerText.includes('課長') || lowerText.includes('社長') ||
        lowerText.includes('リーダー') || lowerText.includes('ボス') || lowerText.includes('主任') ||
        lowerText.includes('係長') || lowerText.includes('マネージャー') || lowerText.includes('管理')) {
      return lowerText.includes('部下') || lowerText.includes('配下') || lowerText.includes('手下') ? 'subordinate' : 'superior'
    }
    
    // 仕事仲間系 (colleague)
    if (lowerText.includes('同僚') || lowerText.includes('仕事仲間') || lowerText.includes('職場') ||
        lowerText.includes('同期') || lowerText.includes('チーム') || lowerText.includes('ビジネス') ||
        lowerText.includes('取引') || lowerText.includes('共同') || lowerText.includes('協業') ||
        lowerText.includes('同じ部署') || lowerText.includes('プロジェクト')) {
      return 'colleague'
    }
    
    // パートナー系 (partner)
    if (lowerText.includes('相棒') || lowerText.includes('相方') || lowerText.includes('バディ') ||
        lowerText.includes('コンビ') || lowerText.includes('協力') || lowerText.includes('共闘') ||
        lowerText.includes('タッグ') || lowerText.includes('ペア') || lowerText.includes('仲間') && lowerText.includes('戦')) {
      return 'partner'
    }
    
    // 友人系 (friend) - より一般的なので後の方で判定
    if (lowerText.includes('友') || lowerText.includes('親友') || lowerText.includes('仲間') ||
        lowerText.includes('仲良') || lowerText.includes('仲が良') || lowerText.includes('フレンド') ||
        lowerText.includes('友好') || lowerText.includes('友情') || lowerText.includes('友達') ||
        lowerText.includes('マブダチ') || lowerText.includes('ダチ') || lowerText.includes('親しい')) {
      return 'friend'
    }
    
    // 知人系 (acquaintance)
    if (lowerText.includes('知人') || lowerText.includes('知り合い') || lowerText.includes('顔見知り') ||
        lowerText.includes('無関心') || lowerText.includes('興味な') || lowerText.includes('他人') ||
        lowerText.includes('無関係') || lowerText.includes('どうでも') || lowerText.includes('単なる') ||
        lowerText.includes('ただの') || lowerText.includes('特に関係') || lowerText.includes('関わりが薄')) {
      return 'acquaintance'
    }
    
    return 'other'
  }
  
  // キーワードベースの関係性タイプを事前計算
  const relationshipTypes: Record<string, string> = {}
  if (result) {
    result.relationships.forEach(rel => {
      const sourceToTargetType = detectRelationType(rel.fromSourceToTarget)
      const targetToSourceType = detectRelationType(rel.fromTargetToSource)
      
      // デバッグ用ログ
      console.log('=== 関係性判定 ===')
      console.log(`サーバー側判定: sourceToTarget=${rel.sourceToTargetType}, targetToSource=${rel.targetToSourceType}`)
      console.log(`${rel.source} → ${rel.target}: "${rel.fromSourceToTarget}"`)
      console.log(`クライアント判定: ${sourceToTargetType} (色: ${RELATIONSHIP_TYPE_COLORS[sourceToTargetType as keyof typeof RELATIONSHIP_TYPE_COLORS]})`)
      console.log(`${rel.target} → ${rel.source}: "${rel.fromTargetToSource}"`)
      console.log(`クライアント判定: ${targetToSourceType} (色: ${RELATIONSHIP_TYPE_COLORS[targetToSourceType as keyof typeof RELATIONSHIP_TYPE_COLORS]})`)
      
      relationshipTypes[`${rel.source}-${rel.target}`] = sourceToTargetType
      relationshipTypes[`${rel.target}-${rel.source}`] = targetToSourceType
    })
  }

  useEffect(() => {
    if (!svgRef.current || !result) return

    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove()

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)

    // Create container for zoom
    const container = svg.append('g')

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        container.attr('transform', event.transform)
      })

    svg.call(zoom)

    // Prepare data
    const nodes = result.characters.map(char => ({
      ...char,
      x: width / 2,
      y: height / 2
    }))

    const links = result.relationships.map(rel => ({
      ...rel,
      source: nodes.find(n => n.id === rel.source)!,
      target: nodes.find(n => n.id === rel.target)!
    }))

    // Create force simulation with more spacing
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id((d: any) => d.id).distance(200))
      .force('charge', d3.forceManyBody().strength(-800))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(80))
      .force('x', d3.forceX(width / 2).strength(0.1))  // 中央に引き寄せる力を追加
      .force('y', d3.forceY(height / 2).strength(0.1)) // 中央に引き寄せる力を追加

    // Create link groups for curved paths (双方向の関係を別々の曲線で表現)
    const linkGroup = container.append('g').attr('class', 'links')
    
    // 各関係に対して2本の曲線パスを作成
    const linkPairs = links.map(link => {
      const group = linkGroup.append('g')
        .attr('class', 'link-pair')
        .style('cursor', 'pointer')
      
      // サーバー側で判定された各方向のタイプを使用、フォールバックとしてキーワード判定を使用
      const sourceToTargetType = link.sourceToTargetType || link.relationshipType || relationshipTypes[`${link.source.id}-${link.target.id}`] || 'other'
      const targetToSourceType = link.targetToSourceType || link.relationshipType || relationshipTypes[`${link.target.id}-${link.source.id}`] || 'other'
      
      // デバッグ：実際に使用される値を確認
      console.log(`=== 描画時の判定 (${link.source.id} ⇄ ${link.target.id}) ===`)
      console.log(`sourceToTargetType: ${sourceToTargetType} (色: ${RELATIONSHIP_TYPE_COLORS[sourceToTargetType as keyof typeof RELATIONSHIP_TYPE_COLORS]})`)
      console.log(`targetToSourceType: ${targetToSourceType} (色: ${RELATIONSHIP_TYPE_COLORS[targetToSourceType as keyof typeof RELATIONSHIP_TYPE_COLORS]})`)
      console.log(`link.sourceToTargetType: ${link.sourceToTargetType}`)
      console.log(`link.targetToSourceType: ${link.targetToSourceType}`)
      console.log(`link.relationshipType: ${link.relationshipType}`)
      
      // Source to Target (上向きカーブ)
      const path1 = group.append('path')
        .attr('stroke', RELATIONSHIP_TYPE_COLORS[sourceToTargetType as keyof typeof RELATIONSHIP_TYPE_COLORS] || '#999')
        .attr('stroke-width', Math.max(3, Math.min(link.strength || 5, 8) / 1.5))
        .attr('fill', 'none')
        .attr('opacity', 0.8)
        .attr('marker-end', 'url(#arrowhead-' + sourceToTargetType + ')')
      
      // Target to Source (下向きカーブ)
      const path2 = group.append('path')
        .attr('stroke', RELATIONSHIP_TYPE_COLORS[targetToSourceType as keyof typeof RELATIONSHIP_TYPE_COLORS] || '#999')
        .attr('stroke-width', Math.max(3, Math.min(link.strength || 5, 8) / 1.5))
        .attr('fill', 'none')
        .attr('opacity', 0.6)
        .attr('stroke-dasharray', '5,5') // 点線で区別
        .attr('marker-end', 'url(#arrowhead-dashed-' + targetToSourceType + ')')
      
      return { group, path1, path2, data: link }
    })

    // 矢印マーカーの定義（全ての関係性タイプに対して生成）
    const defs = svg.append('defs')
    Object.entries(RELATIONSHIP_TYPE_COLORS).forEach(([type, color]) => {
      // 実線用の矢印
      defs.append('marker')
        .attr('id', 'arrowhead-' + type)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 25)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', color)
        .attr('opacity', 0.6)
      
      // 点線用の矢印
      defs.append('marker')
        .attr('id', 'arrowhead-dashed-' + type)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 25)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', color)
        .attr('opacity', 0.4)
    })
    
    // クリックイベントの設定
    linkPairs.forEach(({ group, data }) => {
      group.on('click', (event) => {
        event.stopPropagation()
        const edge: RelationshipEdge = {
          source: data.source.id,
          target: data.target.id,
          relationshipType: data.relationshipType,
          fromSourceToTarget: data.fromSourceToTarget,
          fromTargetToSource: data.fromTargetToSource,
          strength: data.strength,
          description: data.description
        }
        setSelectedEdge(edge)
        setSelectedNode(null)
      })
      .on('mouseenter', function() {
        d3.select(this).selectAll('path')
          .attr('stroke-width', Math.max(3, Math.min(data.strength || 5, 10) / 1.5))
          .attr('opacity', 1)
      })
      .on('mouseleave', function() {
        d3.select(this).select('path:first-child')
          .attr('stroke-width', Math.max(2, Math.min(data.strength || 5, 10) / 2))
          .attr('opacity', 0.6)
        d3.select(this).select('path:last-child')
          .attr('stroke-width', Math.max(2, Math.min(data.strength || 5, 10) / 2))
          .attr('opacity', 0.4)
      })
    })

    // Create node groups
    const node = container.append('g')
      .selectAll('g')
      .data(nodes)
      .enter().append('g')
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation()
        setSelectedNode(d)
        setSelectedEdge(null)
      })
      .call(d3.drag<any, any>()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended))

    // Add circles for nodes
    node.append('circle')
      .attr('r', d => d.isMain ? 30 : 25)
      .attr('fill', d => d.isMain ? '#3b82f6' : '#10b981')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', d => selectedNode?.id === d.id ? '5,5' : 'none')

    // Add labels
    node.append('text')
      .text(d => d.name)
      .attr('text-anchor', 'middle')
      .attr('dy', 40)
      .attr('font-size', '12px')
      .attr('font-weight', d => d.isMain ? 'bold' : 'normal')

    // Add tooltips
    node.append('title')
      .text(d => `${d.name}\n${d.profile || ''}`)

    // Update positions on simulation tick
    simulation.on('tick', () => {
      // 曲線パスの更新
      linkPairs.forEach(({ path1, path2, data }) => {
        const source = data.source as any
        const target = data.target as any
        
        // 2点間の距離と角度を計算
        const dx = target.x - source.x
        const dy = target.y - source.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        
        // カーブの制御点を計算（上下に分ける）
        const curvature = 0.3 // カーブの強さ
        const offsetX = -dy * curvature
        const offsetY = dx * curvature
        
        // Source to Target の曲線（上側）
        const midX1 = (source.x + target.x) / 2 + offsetX
        const midY1 = (source.y + target.y) / 2 + offsetY
        path1.attr('d', `M${source.x},${source.y} Q${midX1},${midY1} ${target.x},${target.y}`)
        
        // Target to Source の曲線（下側）
        const midX2 = (source.x + target.x) / 2 - offsetX
        const midY2 = (source.y + target.y) / 2 - offsetY
        path2.attr('d', `M${target.x},${target.y} Q${midX2},${midY2} ${source.x},${source.y}`)
      })

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`)
    })

    // Drag functions
    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart()
      d.fx = d.x
      d.fy = d.y
    }

    function dragged(event: any, d: any) {
      d.fx = event.x
      d.fy = event.y
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0)
      d.fx = null
      d.fy = null
    }

    // Cleanup
    return () => {
      simulation.stop()
    }
  }, [result, width, height, selectedNode, selectedEdge])

  const getRelationshipsForNode = (nodeId: string) => {
    return result.relationships.filter(rel => 
      rel.source === nodeId || rel.target === nodeId
    )
  }

  return (
    <div className="relative">
      <svg ref={svgRef} className="border rounded-lg bg-white" />
      <div className="absolute top-2 right-2 space-y-2">
        <div className="text-xs text-muted-foreground bg-white/90 p-2 rounded shadow">
          ドラッグで移動・スクロールでズーム・クリックで詳細
        </div>
        <div className="bg-white/90 p-2 rounded shadow">
          <p className="text-xs font-medium mb-1">線の見方:</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-xs">
              <svg width="30" height="10">
                <line x1="0" y1="5" x2="30" y2="5" stroke="#666" strokeWidth="2" />
                <polygon points="28,5 30,3 30,7" fill="#666" />
              </svg>
              <span>実線: A→Bへの感情</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <svg width="30" height="10">
                <line x1="0" y1="5" x2="30" y2="5" stroke="#666" strokeWidth="2" strokeDasharray="5,5" />
                <polygon points="28,5 30,3 30,7" fill="#666" strokeOpacity="0.6" />
              </svg>
              <span>点線: B→Aへの感情</span>
            </div>
          </div>
        </div>
        <div className="bg-white/90 p-2 rounded shadow max-h-48 overflow-auto">
          <p className="text-xs font-medium mb-1">色の意味:</p>
          <div className="grid grid-cols-2 gap-1">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ec4899' }}></div>
              <span className="text-xs">恋愛・元恋人</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#7c2d12' }}></div>
              <span className="text-xs">敵対</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22c55e' }}></div>
              <span className="text-xs">友人</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3b82f6' }}></div>
              <span className="text-xs">家族</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f97316' }}></div>
              <span className="text-xs">ライバル</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8b5cf6' }}></div>
              <span className="text-xs">師弟</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#06b6d4' }}></div>
              <span className="text-xs">相棒・幼馴染</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#fbbf24' }}></div>
              <span className="text-xs">憧れ</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#9ca3af' }}></div>
              <span className="text-xs">仕事関係・その他</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* キャラクター詳細パネル */}
      {selectedNode && (
        <div className="card absolute top-4 left-4 w-80 max-h-96 overflow-auto shadow-lg">
          <div className="p-6 pb-3">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">{selectedNode.name}</h3>
              <button
                onClick={() => setSelectedNode(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-3">
            {selectedNode.isMain && (
              <Badge variant="default">メインキャラクター</Badge>
            )}
            <div>
              <p className="text-sm font-medium mb-1">プロフィール</p>
              <p className="text-sm text-muted-foreground">{selectedNode.profile}</p>
            </div>
            {selectedNode.personality && (
              <div>
                <p className="text-sm font-medium mb-1">性格</p>
                <p className="text-sm text-muted-foreground">{selectedNode.personality}</p>
              </div>
            )}
            {selectedNode.background && (
              <div>
                <p className="text-sm font-medium mb-1">背景</p>
                <p className="text-sm text-muted-foreground">{selectedNode.background}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium mb-2">関係性</p>
              <div className="space-y-2">
                {getRelationshipsForNode(selectedNode.id).map((rel, idx) => {
                  const isSource = rel.source === selectedNode.id
                  const otherNodeId = isSource ? rel.target : rel.source
                  const otherNode = result.characters.find(c => c.id === otherNodeId)
                  const relationText = isSource ? rel.fromSourceToTarget : rel.fromTargetToSource
                  
                  return (
                    <div key={idx} className="p-2 bg-muted rounded text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          variant="outline"
                          style={{
                            borderColor: RELATIONSHIP_TYPE_COLORS[rel.relationshipType as keyof typeof RELATIONSHIP_TYPE_COLORS],
                            color: RELATIONSHIP_TYPE_COLORS[rel.relationshipType as keyof typeof RELATIONSHIP_TYPE_COLORS]
                          }}
                        >
                          {RELATIONSHIP_TYPE_LABELS[rel.relationshipType as keyof typeof RELATIONSHIP_TYPE_LABELS] || rel.relationshipType}
                        </Badge>
                        <span className="font-medium">{otherNode?.name}</span>
                      </div>
                      <p className="text-muted-foreground">{relationText}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 関係性詳細パネル */}
      {selectedEdge && (
        <div className="card absolute bottom-4 left-4 w-[450px] shadow-lg">
          <div className="p-6 pb-3">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">関係性の詳細</h3>
              <button
                onClick={() => setSelectedEdge(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="px-6 pb-6 space-y-4">
            <div className="flex items-center justify-between">
              <Badge
                style={{
                  backgroundColor: RELATIONSHIP_TYPE_COLORS[selectedEdge.relationshipType as keyof typeof RELATIONSHIP_TYPE_COLORS],
                  color: 'white'
                }}
              >
                {RELATIONSHIP_TYPE_LABELS[selectedEdge.relationshipType as keyof typeof RELATIONSHIP_TYPE_LABELS] || selectedEdge.relationshipType}
              </Badge>
              <span className="text-sm font-medium">関係強度: {selectedEdge.strength}/10</span>
            </div>
            
            <div className="space-y-3">
              <div className="relative">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-400 to-blue-200"></div>
                <div className="pl-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-blue-900">
                      {result.characters.find(c => c.id === selectedEdge.source)?.name}
                    </span>
                    <span className="text-blue-400">→</span>
                    <span className="text-sm font-semibold text-blue-900">
                      {result.characters.find(c => c.id === selectedEdge.target)?.name}
                    </span>
                  </div>
                  <p className="text-sm text-blue-700">{selectedEdge.fromSourceToTarget}</p>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-green-400 to-green-200"></div>
                <div className="pl-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-green-900">
                      {result.characters.find(c => c.id === selectedEdge.target)?.name}
                    </span>
                    <span className="text-green-400">→</span>
                    <span className="text-sm font-semibold text-green-900">
                      {result.characters.find(c => c.id === selectedEdge.source)?.name}
                    </span>
                  </div>
                  <p className="text-sm text-green-700">{selectedEdge.fromTargetToSource}</p>
                </div>
              </div>
            </div>
            
            {selectedEdge.description && (
              <div className="pt-2 border-t">
                <p className="text-sm font-medium mb-1">関係性の背景</p>
                <p className="text-sm text-muted-foreground">{selectedEdge.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}